import React, { useState, useEffect } from "react";
import assets from "../../assets";
import { useNavigate } from "react-router-dom";
import { ROUTE_DASHBOARD } from "../../route/communicative";
import useHandler from "./handler";
import { useSingleton } from "../../hooks/use-singleton";
import { SessionSummaryCard } from "../../common-components/score-card";
import "./style.sass";

const TOPICS_BY_DIFFICULTY = {
  Beginner: [
    "My Family & Friends",
    "My Daily Routine",
    "Favorite Hobbies & Games",
    "My School & Classroom",
    "Pets & Animals",
    "Healthy Food & Fruits",
  ],
  Intermediate: [
    "School Science Exhibition",
    "Dream Careers & Goals",
    "Saving the Environment",
    "Importance of Reading Books",
    "Healthy Habits & Sports",
    "Computers & Technology",
  ],
  Advanced: [
    "Impact of Artificial Intelligence",
    "Climate Change & Clean Energy",
    "Social Media: Pros and Cons",
    "Leadership & Student Teamwork",
    "Space Exploration & Discovery",
    "Overcoming Challenges in Life",
  ],
};

const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

export const SpokenPractice = () => {
  const [isStarted, setIsStarted] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [preferences, setPreferences] = useState({
    level: "10",
    topic: "",
    difficulty: "Beginner",
  });
  const navigate = useNavigate();
  const { mail, user } = useSingleton();
  const {
    loading,
    passage,
    evaluation,
    fetchPassage,
    startRecording,
    stopRecording,
    evaluateSpeech,
    setEvaluation,
  } = useHandler();

  const userName = user?.data?.first_name || "Student";

  const handleMicClick = async () => {
    if (isTranscribing) return;
    if (isRecording) {
      setIsRecording(false);
      setIsTranscribing(true);
      const audioBlob = await stopRecording();
      if (audioBlob) {
        evaluateSpeech(audioBlob);
      } else {
        setIsTranscribing(false);
      }
    } else {
      setIsRecording(true);
      startRecording();
    }
  };

  useEffect(() => {
    if (evaluation) {
      setIsTranscribing(false);
    }
  }, [evaluation]);

  useEffect(() => {
    if (isStarted) {
      fetchPassage(preferences);
    }
  }, [isStarted]);

  const currentTopics =
    TOPICS_BY_DIFFICULTY[preferences.difficulty] ||
    TOPICS_BY_DIFFICULTY.Beginner;

  const isReady = preferences.topic?.trim() && preferences.difficulty;

  const handleBackToSetup = () => {
    setIsStarted(false);
    setIsRecording(false);
    setIsTranscribing(false);
    setEvaluation(null);
  };

  return (
    <section className="spoken_practice">
      <header className="spoken_header">
        <div className="header_left">
          <img src={assets.ic_logo} alt="Rovian AI" className="logo" />
          <span className="logo_title">Rovian AI</span>
        </div>
        <div className="back_link" onClick={() => navigate(ROUTE_DASHBOARD)}>
          <span className="back-arrow">←</span>
          <span>Dashboard</span>
        </div>
      </header>

      <div className="spoken_practice_body">
        {!isStarted ? (
          <main className="spoken_setup_card">
            <div className="spoken_card_header">
              <h1 className="spoken_setup__title">Spoken Practice</h1>
              <p className="spoken_setup__subtitle">
                Read aloud short passages to build pronunciation, fluency, and spoken confidence.
              </p>
            </div>

            {/* 1. DIFFICULTY FIRST */}
            <section className="spoken_field">
              <label className="spoken_field__label">Difficulty Level</label>
              <div className="difficulty_grid">
                {DIFFICULTIES.map((d) => {
                  const isActive = preferences.difficulty === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      className={`diff_btn ${isActive ? "active" : ""}`}
                      onClick={() =>
                        setPreferences({ ...preferences, difficulty: d })
                      }
                    >
                      <span className="diff_name">{d}</span>
                      {isActive && <span className="selection_dot"></span>}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* 2. TOPIC & SUGGESTIONS */}
            <section className="spoken_field">
              <div className="field_label_row">
                <label className="spoken_field__label" htmlFor="topic-input">
                  Topic
                </label>
                <span className="field_hint">Pick below or type custom</span>
              </div>

              <div className="topic_input_wrapper">
                <input
                  id="topic-input"
                  type="text"
                  className="spoken_text_input"
                  placeholder="e.g. My Favorite Hobbies, School Trips..."
                  value={preferences.topic}
                  onChange={(e) =>
                    setPreferences({ ...preferences, topic: e.target.value })
                  }
                />
                {preferences.topic && (
                  <button
                    type="button"
                    className="topic_clear_btn"
                    onClick={() =>
                      setPreferences({ ...preferences, topic: "" })
                    }
                    title="Clear topic"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="topic_pills_grid">
                {currentTopics.map((t) => {
                  const isSelected =
                    preferences.topic?.trim().toLowerCase() ===
                    t.toLowerCase();
                  return (
                    <button
                      key={t}
                      type="button"
                      className={`topic_pill ${isSelected ? "selected" : ""}`}
                      onClick={() =>
                        setPreferences({ ...preferences, topic: t })
                      }
                    >
                      <span className="pill_dot"></span>
                      <span className="pill_text">{t}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* START BUTTON */}
            <button
              type="button"
              className="start_practice_btn"
              disabled={!isReady}
              onClick={() => setIsStarted(true)}
            >
              Start Practice
            </button>
          </main>
        ) : (
          <div className="passage_view_container">
            <div className="passage_header_bar">
              <div className="passage_info_group">
                <button
                  type="button"
                  className="spoken_back_btn"
                  onClick={handleBackToSetup}
                >
                  ← Change Topic / Difficulty
                </button>
                <h1 className="passage_page_title">Spoken Practice</h1>
                <p className="passage_page_subtitle">
                  Build pronunciation and fluency by reading the passage aloud into your microphone.
                </p>
              </div>
              <div className="passage_badges">
                <span className="badge difficulty_badge">
                  {preferences.difficulty}
                </span>
                <span className="badge topic_badge">
                  {preferences.topic}
                </span>
              </div>
            </div>

            {loading && !isTranscribing ? (
              <div className="spoken_practice_loading">
                <div className="spoken_practice_spinner" />
                <span>Generating reading passage...</span>
              </div>
            ) : evaluation ? (
              <div className="evaluation_view">
                <SessionSummaryCard
                  score={evaluation?.fluency_score || 0}
                  max={100}
                  title={evaluation?.score_heading}
                  message={evaluation?.overall_feedback}
                  showMax={false}
                  metrics={
                    evaluation.metrics?.map((m) => ({
                      label: m.label,
                      score: m.score,
                      tag: m.tag,
                    })) || []
                  }
                />

                <div className="word_feedback">
                  <div className="word_feedback_header">
                    <h3>Word-by-word Analysis</h3>
                    <div className="legend">
                      <span>
                        <i className="dot green"></i> Correct
                      </span>
                      <span>
                        <i className="dot yellow"></i> Mispronounced
                      </span>
                      <span>
                        <i className="dot purple"></i> Extra
                      </span>
                      <span>
                        <i className="dot gray"></i> Missed
                      </span>
                    </div>
                  </div>
                  <div className="words_list">
                    {evaluation.word_results?.map((res, i) => (
                      <span key={i} className={`word_item ${res.status}`}>
                        {res.word}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="tips_section">
                  <div className="word_feedback_header">
                    <h3>Practice Tips</h3>
                  </div>
                  <div className="tips_container">
                    <table className="tips_table">
                      <tbody>
                        {evaluation.practice_tips?.map((tip, i) => (
                          <tr key={i}>
                            <td className="tip_word">{tip.word}</td>
                            <td className="tip_text">{tip.tip}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              passage && (
                <div className="passage_active_card">
                  <div className="passage_card_body">
                    <h2 className="passage_title">{passage.title}</h2>
                    <p className="passage_text">{passage.passage}</p>
                  </div>

                  <div className="recording_dock">
                    <div
                      className={`mic_btn ${isRecording ? "recording" : ""} ${
                        isTranscribing ? "transcribing" : ""
                      }`}
                      onClick={handleMicClick}
                    >
                      {isTranscribing ? (
                        <div className="loader_spinner" />
                      ) : isRecording ? (
                        <div className="stop_icon" />
                      ) : (
                        <img
                          src={assets.ic_mic_white}
                          alt="mic"
                          width={24}
                          height={24}
                        />
                      )}
                    </div>
                    <span className="mic_status_label">
                      {isTranscribing
                        ? "Evaluating speech..."
                        : isRecording
                          ? "Recording... Tap to evaluate"
                          : "Tap to speak"}
                    </span>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
};
